<template>
  <v-card variant="text">
    <v-card-text>
      <div class="text-caption mb-3">
        Gemeinsam über alle Nutzer · Relative Stärke, keine Kauf-/Verkaufssignale
        <div v-if="snapshot?.generated_at">Ranking erstellt: {{ formatTimestamp(snapshot.generated_at) }}</div>
      </div>
      <div v-if="loading" role="status" class="py-4">Lade Top 4 / Flop 4…</div>
      <v-alert v-else-if="error" type="error" variant="tonal">{{ error }}</v-alert>
      <v-alert v-else-if="snapshot?.status !== 'ok'" type="info" variant="tonal">
        {{ statusMessage }}
      </v-alert>
      <template v-else>
        <div class="text-caption mb-3">
          {{ snapshot.eligible_count }} geeignete Produkte · {{ snapshot.excluded_count }} ausgeschlossen
        </div>
        <v-row>
          <v-col v-for="side in sides" :key="side.key" cols="12" md="6">
            <h3 class="text-subtitle-1">{{ side.title }}</h3>
            <div class="text-caption text-medium-emphasis mb-2">{{ side.description }}</div>
            <div v-if="!snapshot[side.key]?.length" class="text-medium-emphasis">Keine weiteren Produkte vorhanden.</div>
            <v-card v-for="item in snapshot[side.key]" :key="item.isin" class="product-entry mb-2" :class="{ 'product-entry--flop': side.key === 'flop' }">
              <v-card-text class="py-3">
              <div class="d-flex align-start justify-space-between ga-2">
                <div class="d-flex align-center flex-wrap ga-2 product-entry-heading">
                  <v-chip size="small" :color="side.key === 'flop' ? 'red-lighten-3' : 'light-blue-lighten-3'">{{ side.rankLabel }} {{ item.rank }}</v-chip>
                  <strong class="text-body-1">{{ item.stocks_name }}</strong>
                  <v-chip size="x-small" variant="outlined">{{ item.product_type }}</v-chip>
                </div>
                <div class="text-caption text-no-wrap flex-shrink-0" title="Buy-Score V2">Score V2: <strong>{{ number(item.buy_score_v2) }}</strong></div>
              </div>
              <div class="d-flex flex-wrap ga-4 mt-2">
                <div class="text-caption">
                Jahresperformance: <strong>{{ percent(productPerformance(item, 'perf_jahr')) }}</strong>
                · Monat: <strong>{{ percent(productPerformance(item, 'perf_monat')) }}</strong>
                </div>
                <div class="text-caption" title="Levy: letzter verwendeter Kurs geteilt durch den Durchschnitt der letzten 26 abgeschlossenen Kalenderwochen. 1,10 bedeutet 10 % über dem Durchschnitt.">
                  RSL (26 Wochen): <strong>{{ rsl(item.rsl_26w) }}</strong>
                </div>
              </div>
              <div class="text-caption mt-1">{{ performanceInfo(item) }}</div>
              <div class="text-caption mt-1">
                Momentum (20 Handelstage): <strong>{{ number(item.momentum_20_pct) }} %</strong>
                · Änderung (5 Handelstage): <strong>{{ number(item.momentum_change_5_pp) }} PP</strong>
              </div>
              <div class="text-caption">{{ item.momentum_status }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import api from '@/api/client';

export default {
  name: 'ProductRankingsCard',
  data() {
    return {
      snapshot: null,
      loading: false,
      error: null,
      sides: [
        { key: 'top', title: 'Top 4', rankLabel: 'Top', description: 'Höchste V2-Scores · stärkster zuerst' },
        { key: 'flop', title: 'Flop 4', rankLabel: 'Flop', description: 'Niedrigste V2-Scores · schwächster zuerst' },
      ],
    };
  },
  computed: {
    statusMessage() {
      return {
        empty: 'Keine geeigneten Produkte vorhanden.',
        missing: 'Ranking noch nicht berechnet.',
        stale: 'Ranking veraltet. Eine aktuelle Berechnung steht noch aus.',
        invalid: 'Ranking derzeit nicht verfügbar.',
      }[this.snapshot?.status] || 'Ranking derzeit nicht verfügbar.';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    rsl(value) {
      if (value == null || value === '' || !Number.isFinite(Number(value))) return '—';
      return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },
    productPerformance(item, field) {
      const asNumber = value => value == null || value === '' || !Number.isFinite(Number(value))
        ? null : Number(value);
      const direct = asNumber(item[field]);
      if (direct !== null) return direct;

      const normalize = value => String(value || '').trim().toUpperCase();
      const isin = normalize(item.isin);
      const stocks = this.$store.getters.stateStocks || [];
      const matchingStocks = Array.isArray(stocks)
        ? stocks.filter(stock => isin && normalize(stock.stocks_ISIN) === isin) : [];
      const ids = new Set(matchingStocks.map(stock => String(stock.idstocks)));
      const names = new Set([item.stocks_name, ...matchingStocks.map(stock => stock.stocks_name)]
        .map(normalize).filter(Boolean));
      const rows = this.$store.getters.stateDailyTradesAll?.rows || [];
      const matchingRows = Array.isArray(rows) ? rows.filter(row => {
        const rowIsin = normalize(row.isin || row.stocks_ISIN);
        if (rowIsin) return isin && rowIsin === isin;
        const id = row.stock_id ?? row.idstocks ?? row.stocks_idstocks;
        if (id != null && ids.size) return ids.has(String(id));
        return names.has(normalize(row.stocks_name));
      }) : [];
      const values = new Set(matchingRows.map(row => asNumber(row[field])).filter(value => value !== null));
      // Conflicting rows must not produce an arbitrary product performance.
      return values.size === 1 ? [...values][0] : null;
    },
    percent(value) {
      if (value == null || value === '' || !Number.isFinite(Number(value))) return '—';
      return `${new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero',
      }).format(Number(value))} %`;
    },
    performanceInfo(item) {
      const year = this.productPerformance(item, 'perf_jahr');
      if (year === null) return 'Zur Jahresperformance liegen noch keine eindeutigen Daten vor.';
      const yearText = year > 0 ? 'Die Jahresperformance ist positiv.'
        : year < 0 ? 'Die Jahresperformance ist negativ.' : 'Die Jahresperformance liegt bei 0 %.';
      const momentum = item.momentum_20_pct;
      if (momentum == null || momentum === '' || !Number.isFinite(Number(momentum))) return yearText;
      const recentText = Number(momentum) > 0 ? 'Auch über die letzten 20 Handelstage ist der Kurs gestiegen.'
        : Number(momentum) < 0 ? 'Über die letzten 20 Handelstage ist der Kurs gefallen.'
          : 'Über die letzten 20 Handelstage ist der Kurs unverändert.';
      return `${yearText} ${year <= 0 ? recentText.replace('Auch über', 'Über') : recentText}`;
    },
    async load() {
      if (this.loading) return;
      this.loading = true;
      this.error = null;
      this.snapshot = null;
      try {
        const { data } = await api.get('portfolio/product-rankings');
        this.snapshot = data;
      } catch (error) {
        this.error = 'Produktranking konnte nicht geladen werden. Bitte erneut aktualisieren.';
      } finally {
        this.loading = false;
      }
    },
    number(value) {
      if (value == null || !Number.isFinite(Number(value))) return '—';
      return Number(value).toLocaleString('de-DE', { maximumFractionDigits: 3 });
    },
    formatTimestamp(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('de-DE');
    },
  },
};
</script>

<style scoped>
.product-entry-heading {
  min-width: 0;
  overflow-wrap: anywhere;
}

.product-entry {
  background-color: #263540;
  color: #fff;
  border-left: 3px solid #64b5f6;
  transition: background-color 0.15s ease;
}

.product-entry:hover {
  background-color: #304454;
}

.product-entry.product-entry--flop {
  border-left-color: #ef9a9a;
}

</style>
